import React from 'react';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps } from '../../core/tools/interfaces/ITool';
import { ICommand } from '../../core/commands/ICommand';
import { ChangeNodeStyleCommand } from '../../core/commands/ChangeNodeStyleCommand';
import { CompositeCommand } from '../../core/commands/ICommand';
import { Button } from '../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Separator } from '../../components/ui/separator';
import { Bold, Italic, Underline, Strikethrough } from 'lucide-react';
import { cn } from '../../lib/utils';
import { TOOL_APPLICABILITY } from '../../core/tools/config/toolApplicability';
import { useTranslation } from '../../hooks/useTranslation';

interface TextStyleState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
}

type TextStyleParams = Partial<TextStyleState>;

const TextStyleToolComponent: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const [open, setOpen] = React.useState(false);
  const [styles, setStyles] = React.useState<TextStyleState>({
    bold: (context.selectionStyles?.fontWeight || '').toLowerCase() === 'bold',
    italic: (context.selectionStyles?.fontStyle || '').toLowerCase() === 'italic',
    underline: (context.selectionStyles?.textDecoration || '').includes('underline'),
    strikethrough: (context.selectionStyles?.textDecoration || '').includes('line-through')
  });
  React.useEffect(() => {
    setStyles({
      bold: (context.selectionStyles?.fontWeight || '').toLowerCase() === 'bold',
      italic: (context.selectionStyles?.fontStyle || '').toLowerCase() === 'italic',
      underline: (context.selectionStyles?.textDecoration || '').includes('underline'),
      strikethrough: (context.selectionStyles?.textDecoration || '').includes('line-through')
    });
  }, [context.selectionStyles?.fontWeight, context.selectionStyles?.fontStyle, context.selectionStyles?.textDecoration]);
  
  const selectedCount = context.selection.size;
  const isDisabled = selectedCount === 0;

  const toggleStyle = (style: keyof TextStyleState) => {
    const newStyles = { ...styles, [style]: !styles[style] };
    setStyles(newStyles);
    const params: TextStyleParams = style === 'bold'
      ? { bold: newStyles.bold }
      : style === 'italic'
        ? { italic: newStyles.italic }
        : style === 'underline'
          ? { underline: newStyles.underline }
          : { strikethrough: newStyles.strikethrough };
    onExecute(params);
  };


  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="tid.editor.shapeToolbar.textStyle"
          disabled={isDisabled}
          className={cn(
            "w-8 h-8 flex items-center justify-center rounded hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
            styles.bold && "bg-muted"
          )}
          aria-label={t('properties.textStyle', { defaultValue: 'Text style' })}
        >
          <span className="font-bold text-base text-foreground">B</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-2" align="start">
        <div className="space-y-1">
          {/* Bold */}
          <Button
            variant={styles.bold ? 'secondary' : 'ghost'}
            size="sm"
            className="w-full justify-start h-8"
            data-testid="tid.editor.textStyle.bold"
            onClick={() => toggleStyle('bold')}
          >
            <Bold className="h-4 w-4 mr-2" />
            <span className="flex-1 text-left text-xs">{t('properties.bold', { defaultValue: 'Bold' })}</span>
            <kbd className="text-xs opacity-50">⌘B</kbd>
          </Button>
          
          {/* Italic */}
          <Button
            variant={styles.italic ? 'secondary' : 'ghost'}
            size="sm"
            className="w-full justify-start h-8"
            data-testid="tid.editor.textStyle.italic"
            onClick={() => toggleStyle('italic')}
          >
            <Italic className="h-4 w-4 mr-2" />
            <span className="flex-1 text-left text-xs">{t('properties.italic', { defaultValue: 'Italic' })}</span>
            <kbd className="text-xs opacity-50">⌘I</kbd>
          </Button>
          
          {/* Underline */}
          <Button
            variant={styles.underline ? 'secondary' : 'ghost'}
            size="sm"
            className="w-full justify-start h-8"
            data-testid="tid.editor.textStyle.underline"
            onClick={() => toggleStyle('underline')}
          >
            <Underline className="h-4 w-4 mr-2" />
            <span className="flex-1 text-left text-xs">{t('properties.underline', { defaultValue: 'Underline' })}</span>
            <kbd className="text-xs opacity-50">⌘U</kbd>
          </Button>
          
          <Separator />
          
          {/* Strikethrough */}
          <Button
            variant={styles.strikethrough ? 'secondary' : 'ghost'}
            size="sm"
            className="w-full justify-start h-8"
            data-testid="tid.editor.textStyle.strikethrough"
            onClick={() => toggleStyle('strikethrough')}
          >
            <Strikethrough className="h-4 w-4 mr-2" />
            <span className="flex-1 text-left text-xs">{t('properties.strikethrough', { defaultValue: 'Strikethrough' })}</span>
          </Button>
          
          <Separator />
          
          {/* Quick preset buttons */}
          <div className="flex gap-1 pt-1">
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "flex-1 h-8 p-0 text-xs font-bold",
                styles.bold && "bg-secondary"
              )}
              onClick={() => toggleStyle('bold')}
            >
              B
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "flex-1 h-8 p-0 text-xs italic",
                styles.italic && "bg-secondary"
              )}
              onClick={() => toggleStyle('italic')}
            >
              I
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "flex-1 h-8 p-0 text-xs underline",
                styles.underline && "bg-secondary"
              )}
              onClick={() => toggleStyle('underline')}
            >
              U
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "flex-1 h-8 p-0 text-xs line-through",
                styles.strikethrough && "bg-secondary"
              )}
              onClick={() => toggleStyle('strikethrough')}
            >
              S
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export class TextStyleTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'text-style',
    name: 'tools.textStyle',
    icon: 'bold',
    category: 'style',
    description: 'Change text formatting styles',
    version: '1.0.0',
    applicability: TOOL_APPLICABILITY.TEXT_SHAPES,
    
    requiresSelection: true,
    minSelection: 1,
    
    ui: {
      toolbar: true,
      contextMenu: true,
      propertyPanel: true,
      sidebar: false,
      floatingPanel: true,
      statusBar: false
    },
    
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true
    }
  };

  canExecute(context: IToolContext): boolean {
    return super.canExecute(context) && context.selection.size > 0;
  }

  execute(params?: TextStyleParams): ICommand | null {
    if (!this.context || this.context.selection.size === 0 || !params) {
      return null;
    }

    const selectedIds = Array.from(this.context.selection);
    const commands: ICommand[] = [];
    
    // Map styles to font weight and style
    if (params.bold !== undefined) {
      commands.push(new ChangeNodeStyleCommand(selectedIds, 'fontWeight', params.bold ? 'bold' : 'normal'));
    }
    
    if (params.italic !== undefined) {
      commands.push(new ChangeNodeStyleCommand(selectedIds, 'fontStyle', params.italic ? 'italic' : 'normal'));
    }
    
    if (params.underline !== undefined || params.strikethrough !== undefined) {
      const currentDecoration = `${this.context.selectionStyles?.textDecoration ?? ''}`.toLowerCase();
      const nextUnderline = params.underline ?? currentDecoration.includes('underline');
      const nextStrikethrough = params.strikethrough ?? currentDecoration.includes('line-through');
      const nextDecoration = [
        nextUnderline ? 'underline' : null,
        nextStrikethrough ? 'line-through' : null,
      ].filter(Boolean).join(' ') || 'none';
      commands.push(new ChangeNodeStyleCommand(selectedIds, 'textDecoration', nextDecoration));
    }

    return commands.length > 0 ? new CompositeCommand('Change Text Style', commands) : null;
  }

  getToolbarComponent() {
    return TextStyleToolComponent;
  }

  getFloatingPanelComponent() {
    return TextStyleToolComponent;
  }
}
